import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import Query from 'dashboard-services/api/Query';

import getApiConfig from 'api/ApiConfig';
import { shedQueries } from 'queries/modules/shed';

export default (() => {
  const useGenerateHtml = ({ defaultContent, sourceFileInputs }) => {
    const dispatch = useDispatch(),
          signal = useRef(),
          getQueryBuilder = useCallback(() => {
            const controller = new AbortController()
            signal.current = controller;
            return new Query.Builder(dispatch(getApiConfig()))
              .withOnParse(response => response.text())
              .withFetching(false)
              .withShouldTimeOut(false)
              .withShouldDisplayError(false)
              .withSignal(controller.signal)
          }
          , [dispatch]),
          { mutateAsync, isPending } = shedQueries.useGenerateHtml({
            getQueryBuilder, 
            template: defaultContent, 
            sourceFileInputs
          }),
          onCall = useCallback((...args) => mutateAsync(...args), [mutateAsync])

    useEffect(() => () => {
      signal.current?.abort?.()
    }, [])

    return {
      onCall,
      isPending,
      signal
    }
  }

  const useGeneratePdf = () => {
    const dispatch = useDispatch(),
          signal = useRef(),
          getQueryBuilder = useCallback(() => {
            const controller = new AbortController()
            signal.current = controller;
            return new Query.Builder(dispatch(getApiConfig()))
              .withOnParse(r => r.arrayBuffer())
              .withFetching(false)
              .withShouldTimeOut(false)
              .withShouldDisplayError(false)
              .withSignal(controller.signal)
          }, [dispatch]),
          { mutateAsync, isPending } = shedQueries.useGeneratePdf({ getQueryBuilder }),
          onCall = useCallback((...args) => mutateAsync(...args), [mutateAsync])

    useEffect(() => () => {
      signal.current?.abort?.()
    }, [])

    return {
      onCall,
      isPending,
      signal
    }
  }

  const useGeneratePdfContent = ({ defaultContent, isPdfGenerationEnabled, generateHtmlCall, generatePdfCall }) => {
    const onCall = useCallback(() => {
            if (!!isPdfGenerationEnabled && defaultContent && defaultContent !== " ") {
              return generateHtmlCall()
                .then(html => generatePdfCall({ html }))
                .then(pdfBlob => pdfBlob.arrayBuffer())
                .then(async buffer => {
                  const { Buffer } = await import("buffer"),
                        base64 = Buffer.from(new Uint8Array(buffer)).toString("base64")
  
                  return base64
                })
                .catch(err => console.error("[PDF] generation failed", {
                  message: err?.message,
                  name: err?.name,
                  err
                }))
            }
          }, [defaultContent, generateHtmlCall, generatePdfCall, isPdfGenerationEnabled])
  
    return { onCall }
  }

  return {
    useGenerateHtml,
    useGeneratePdf,
    useGeneratePdfContent
  }
})()